import {
  Fail, List, None, Some, Success,
} from 'monet';

/**
 * @description Элемент списка листа ожидания.
 * @param {string} dateEnds     Дата окончания.
 * @param {string} employeeName Имя сотрудника.
 * @param {number} id           Идентификатор.
 * @param {string} patientName  Имя пациента.
 * @param {string} phone        Номер пациента.
 * @constructor
 */
export function WaitingItem(dateEnds, employeeName, id, patientName, phone) {
  this.dateEnds = dateEnds;
  this.employeeName = employeeName;
  this.id = id;
  this.patientName = patientName;
  this.phone = phone;
  this.details = null;
  this.showDetails = false;
  this.isAwaitRequest = false;
}

/**
 * @description Элемент списка листа ожидания.
 * @param {string}   comment      Примечание
 * @param {string}   date         Дата окончания.
 * @param {string}   employeeName Имя сотрудника.
 * @param {number}   id           Идентификатор.
 * @param {string}   patientName  Имя пациента.
 * @param {string}   phone        Номер пациента.
 * @param {string}   time         Время начала.
 * @param {string[]} serviceNames Названия услуг.
 * @param {string}   status       Статус.
 * @constructor
 */
export function WaitingItemForWaitingList(
  comment,
  date,
  employeeName,
  id,
  patientName,
  phone,
  time,
  serviceNames,
  status,
) {
  this.comment = comment;
  this.date = date;
  this.employeeName = employeeName;
  this.id = id;
  this.patientName = patientName;
  this.phone = phone;
  this.time = time;
  this.serviceNames = serviceNames;
  this.status = status;
}

/**
 * @description Запись из листа ожидания.
 * @param {string}  comment           Комментарий.
 * @param {Date}    dateEnds          Дата окончания.
 * @param {Date}    dateStarts        Дата начала.
 * @param {?number} employeeId        Идентификатор сотрудника.
 * @param {string}  employeeName      Имя сотрудника.
 * @param {string}  employeePositions Должности сотрудника.
 * @param {?number} id                Идентификатор.
 * @param {?number} patientId         Идентификатор пациента.
 * @param {string}  patientName       Имя пациента.
 * @param {string}  patientPhone      Мобильный номер телефона.
 * @param {array}   services          Идентификатор услуги.
 * @param {?string} timeEnds          Время окончания.
 * @param {?string} timeStarts        Время начала.
 * @param {?string} status            Статус.
 * @param {string}  patientFullName   ФИО пациента.
 * @constructor
 */
export function Waiting(
  comment = '',
  dateEnds = new Date(),
  dateStarts = new Date(),
  employeeId = null,
  employeeName = '',
  employeePositions = '',
  id = null,
  patientId = null,
  patientName = '',
  patientPhone = '',
  services = [],
  timeEnds = '',
  timeStarts = '',
  status = '',
  patientFullName = '',
) {
  this.comment = comment;
  this.dateEnds = dateEnds;
  this.dateStarts = dateStarts;
  this.employeeId = employeeId;
  this.employeeName = employeeName;
  this.employeePositions = employeePositions;
  this.id = id;
  this.patientId = patientId;
  this.patientName = patientName;
  this.patientFullName = patientFullName;
  this.patientPhone = patientPhone;
  this.services = services;
  this.timeEnds = timeEnds;
  this.timeStarts = timeStarts;
  this.status = status;
}

/**
 * @description Запрос на добавление записи в лист ожидания с новым пациентом.
 * @param {string}  birthAt     Дата рождения пациента.
 * @param {string}  comment     Комментарий.
 * @param {string}  dateEnds    Дата окончания.
 * @param {string}  dateStarts  Дата начала.
 * @param {?number} employeeId  Идентификатор сотрудника.
 * @param {string}  name        Имя пациента.
 * @param {string}  patronymic  Отчество пациента.
 * @param {Object}  phone       Номер пациента.
 * @param {?Array}  serviceIds  Идентификатор услуги.
 * @param {string}  surname     Фамилия пациента.
 * @param {string}  timeEnds    Время окончания.
 * @param {string}  timeStarts  Время начала.
 * @constructor
 */
export function WaitingNewPatientRequest(
  birthAt,
  comment,
  dateEnds,
  dateStarts,
  employeeId,
  name,
  patronymic,
  phone,
  serviceIds,
  surname,
  timeEnds,
  timeStarts,
) {
  this.toQuery = () => ({
    birth_at: birthAt,
    comment,
    date_ends: dateEnds,
    date_starts: dateStarts,
    employee_id: employeeId,
    name,
    patronymic,
    phone_value: phone.value,
    phone_type: phone.type,
    service_ids: serviceIds,
    surname,
    time_ends: timeEnds,
    time_starts: timeStarts,
  });
}

/**
 * @description Запрос на добавление записи в лист ожидания с существующим пациентом.
 * @param {string}  comment     Комментарий.
 * @param {string}  dateEnds    Дата окончания.
 * @param {string}  dateStarts  Дата начала.
 * @param {?number} employeeId  Идентификатор сотрудника.
 * @param {number}  patientId   Идентификатор пациента.
 * @param {?Array}  serviceIds  Идентификатор услуги.
 * @param {string}  timeEnds    Время окончания.
 * @param {string}  timeStarts  Время начала.
 * @constructor
 */
export function WaitingExistPatientRequest(
  comment,
  dateEnds,
  dateStarts,
  employeeId,
  patientId,
  serviceIds,
  timeEnds,
  timeStarts,
) {
  this.toQuery = () => ({
    comment,
    date_ends: dateEnds,
    date_starts: dateStarts,
    employee_id: employeeId,
    patient_id: patientId,
    service_ids: serviceIds,
    time_ends: timeEnds,
    time_starts: timeStarts,
  });
}

/**
 * @description Создать ожидание из JSON.
 * @param {Object} waiting Ожидание.
 * @returns {Waiting}
 */
const makeWaitingFromJSON = (waiting) => new Waiting(
  waiting.comment,
  new Date(waiting.date_ends),
  new Date(waiting.date_starts),
  waiting.employee_id,
  waiting.employee_name,
  waiting.employee_positions,
  waiting.id,
  waiting.patient_id,
  waiting.patient_name,
  waiting.patient_phone,
  waiting.services,
  waiting.time_ends,
  waiting.time_starts,
  waiting.status,
  waiting.patient_full_name,
);

/**
 * @description Шлюз листа ожидания.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function WaitingListGateway(axios) {
  /**
   * @description Добавить пациента в лист ожидания.
   * @param {WaitingExistPatientRequest} request Запрос.
   * @returns {Promise<Validation>}
   */
  this.createWithExistPatient = (request) => axios
    .post('/api/waitings', request.toQuery())
    .then(
      async (response) => Success(response.data),
      async (error) => {
        const makeCauses = (causes) => (causes || []).map((cause) => [cause.source, cause.details]);
        if (error.isAxiosError && error.response.status === 400) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 403) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        throw error;
      },
    );
  /**
   * @description Создать и добавить пациента в лист ожидания.
   * @param {WaitingNewPatientRequest} request Запрос.
   * @returns {Promise<Validation>}
   */
  this.createWithNewPatient = (request) => axios
    .post('/api/patients/waitings', request.toQuery())
    .then(
      async (response) => Success(response.data),
      async (error) => {
        const makeCauses = (causes) => (causes || []).map((cause) => [cause.source, cause.details]);
        if (error.isAxiosError && error.response.status === 400) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 403) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        throw error;
      },
    );
  /**
   * @description Получить запись по идентификатору.
   * @param {number} id Идентификатор.
   * @returns {Promise<Validation>}
   */
  this.getById = (id) => axios
    .get(`/api/waitings/${id}`)
    .then(
      async (response) => {
        const waiting = makeWaitingFromJSON(response.data);
        return Success(waiting);
      },
      async (error) => {
        if (error.isAxiosError && [403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        throw error;
      },
    );
  /**
   * @description Получить свободное время для записи.
   * @param {number} waitingId Идентификатор записи.
   * @returns {Promise<List<any>>}
   */
  this.getOpenTimes = (waitingId) => axios
    .get(`/api/waitings/${waitingId}/open-times`)
    .then((response) => List.fromArray(response.data.data));

  /**
   * @description Получить весь список ожиданий
   * @return {Promise<Maybe<{list: WaitingItemForWaitingList[]}> | Maybe<unknown>>}
   */
  this.getWaitingList = () => axios
    .get('/api/waitings')
    .then(
      async (response) => {
        const { actions } = response.data;
        const list = (response.data.data || []).map((item) => new WaitingItemForWaitingList(
          item.comment,
          item.date,
          item.employee_name,
          item.id,
          item.patient_name,
          item.phone,
          item.time,
          item.service_names,
          item.status,
        ));
        return Some({ actions, list });
      },
      async () => None(),
    );

  /**
   * @description Просмотр записей из листа ожидания на текущий день.
   * @returns {Promise<Maybe>}
   */
  this.viewDailyList = (date, period, patientId) => {
    const params = {};
    if (period) {
      params.period = period;
    }
    if (patientId) {
      params.patient_id = patientId;
    }
    return axios
      .get(`/api/waitings/period/${date}`, { params })
      .then(
        async (response) => {
          const actions = response.data.actions || [];
          const list = (response.data.data || []).map(makeWaitingFromJSON);
          return Some({ actions, list });
        },
        async () => None(),
      );
  };
  /**
   * @description Редактировать данные записи из листа ожидания.
   * @param {WaitingExistPatientRequest} request  Запрос.
   * @param {number}                     id Идентификатор записи.
   * @returns {Promise<Validation>}
   */
  this.editWithExistPatient = (request, id) => axios
    .put(`/api/waitings/${id}`, request.toQuery())
    .then(
      async (response) => Success(response.data),
      async (error) => {
        const makeCauses = (causes) => (causes || []).map((cause) => [cause.source, cause.details]);
        if (error.isAxiosError && error.response.status === 400) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 403) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        throw error;
      },
    );
  /**
   * @description Редактировать данные записи из листа ожидания.
   * @param {WaitingNewPatientRequest} request  Запрос.
   * @param {number}                   id Идентификатор записи.
   * @returns {Promise<Validation>}
   */
  this.editWithNewPatient = (request, id) => axios
    .put(`/api/waitings/${id}/new-patient`, request.toQuery())
    .then(
      async (response) => Success(response.data),
      async (error) => {
        const makeCauses = (causes) => (causes || []).map((cause) => [cause.source, cause.details]);
        if (error.isAxiosError && error.response.status === 400) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 403) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          const causes = makeCauses(error.response.data.causes);
          return Fail({ causes, title });
        }
        throw error;
      },
    );
  /**
   * @description Удалить запись из листа ожидания.
   * @param {number} id Идентификатор.
   * @returns {Promise<Validation>}
   */
  this.delete = (id) => axios.delete(`/api/waitings/${id}`).then(
    async (response) => Success(response.data),
    async (error) => {
      if (error.isAxiosError && error.response.status === 403) {
        const { title } = error.response.data;
        return Fail({ title });
      }
      if (error.isAxiosError && error.response.status === 404) {
        const { title } = error.response.data;

        return Fail({ title });
      }
      throw error;
    },
  );
}
